<template>
  <div class="dashboard-integracao">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Configurações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Dashboard Integrações</span>
    </nav>

    <div class="d-flex justify-content-end mb-1">
      <b-button size="sm" variant="outline-primary" @click="carregar" :disabled="carregando">
        <feather-icon icon="RefreshCwIcon" size="13" /> Atualizar
      </b-button>
    </div>

    <div v-if="carregando && !data" class="text-center py-5">
      <b-spinner /> <div class="text-muted mt-1">Carregando KPIs…</div>
    </div>

    <template v-else-if="data">
      <!-- KPIs principais -->
      <b-row class="mb-1">
        <b-col md="2" sm="6" cols="6"><kpi label="Integrações" :value="data.resumo.integracoes_ativas" :sub="`de ${data.resumo.total_integracoes} cadastradas`" icon="ServerIcon" variant="primary" /></b-col>
        <b-col md="2" sm="6" cols="6"><kpi label="Jobs ativos" :value="data.resumo.jobs_ativos" :sub="`${data.resumo.jobs_pausados} pausados`" icon="ClockIcon" variant="info" /></b-col>
        <b-col md="2" sm="6" cols="6"><kpi label="Execuções 24h" :value="data.janelas.ultimas_24h.total || 0" icon="ActivityIcon" variant="secondary" /></b-col>
        <b-col md="2" sm="6" cols="6"><kpi label="Sucesso 24h" :value="formatTaxa(data.janelas.ultimas_24h.taxa_sucesso)" icon="CheckCircleIcon" variant="success" /></b-col>
        <b-col md="2" sm="6" cols="6"><kpi label="Erros 24h" :value="data.janelas.ultimas_24h.erro || 0" icon="AlertTriangleIcon" variant="danger" /></b-col>
        <b-col md="2" sm="6" cols="6"><kpi label="Tempo médio 24h" :value="formatMs(data.janelas.ultimas_24h.tempo_medio_ms)" icon="ZapIcon" variant="warning" /></b-col>
      </b-row>

      <!-- Janelas comparativas -->
      <b-card class="mb-1">
        <h6 class="mb-1">Comparativo por janela</h6>
        <b-table :items="janelas" :fields="janelaFields" small responsive>
          <template #cell(taxa_sucesso)="{ value }">
            <b-badge :variant="taxaVariant(value)">{{ formatTaxa(value) }}</b-badge>
          </template>
          <template #cell(tempo_medio_ms)="{ value }">{{ formatMs(value) }}</template>
        </b-table>
      </b-card>

      <b-row>
        <b-col md="6">
          <b-card no-body class="mb-1">
            <b-card-header><h6 class="mb-0">Top jobs (últimos 7 dias)</h6></b-card-header>
            <b-card-body>
              <div v-if="!data.top_jobs.length" class="text-muted text-center py-2">
                Sem execuções recentes.
              </div>
              <b-table v-else :items="data.top_jobs" :fields="topJobsFields" small hover>
                <template #cell(nome)="{ item }">
                  <router-link :to="{ name: 'jobs-erp-execucoes', params: { id: item.jobid } }">{{ item.nome }}</router-link>
                </template>
                <template #cell(sucessos)="{ value }">
                  <b-badge variant="light-success">{{ value || 0 }}</b-badge>
                </template>
                <template #cell(erros)="{ value }">
                  <b-badge variant="light-danger">{{ value || 0 }}</b-badge>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card no-body class="mb-1">
            <b-card-header><h6 class="mb-0">Próximos disparos</h6></b-card-header>
            <b-card-body>
              <div v-if="!data.proximos_disparos.length" class="text-muted text-center py-2">
                Nenhum job agendado.
              </div>
              <ul v-else class="list-unstyled mb-0">
                <li v-for="p in data.proximos_disparos" :key="p.jobid" class="d-flex justify-content-between py-50 border-bottom">
                  <div>
                    <router-link :to="{ name: 'jobs-erp-execucoes', params: { id: p.jobid } }">{{ p.nome }}</router-link>
                    <div class="text-muted small"><code>{{ p.cron_expression }}</code></div>
                  </div>
                  <div class="text-right small">{{ formatDate(p.proximo_disparo) }}</div>
                </li>
              </ul>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-card no-body class="mb-1">
        <b-card-header><h6 class="mb-0">Últimos erros</h6></b-card-header>
        <b-card-body>
          <div v-if="!data.ultimos_erros.length" class="text-success text-center py-2">
            <feather-icon icon="CheckCircleIcon" /> Sem erros recentes.
          </div>
          <b-table v-else :items="data.ultimos_erros" :fields="errosFields" small hover responsive>
            <template #cell(iniciado_em)="{ value }">{{ formatDate(value) }}</template>
            <template #cell(job_nome)="{ item }">
              <router-link :to="{ name: 'jobs-erp-execucoes', params: { id: item.jobid } }">
                {{ item.job_nome || `Job #${item.jobid}` }}
              </router-link>
            </template>
            <template #cell(status_http)="{ value }">
              <b-badge variant="light-danger">{{ value || '—' }}</b-badge>
            </template>
            <template #cell(tempo_ms)="{ value }">{{ formatMs(value) }}</template>
            <template #cell(origem)="{ value }">
              <b-badge :variant="value === 'MANUAL' ? 'light-info' : 'light-primary'">{{ value }}</b-badge>
            </template>
            <template #cell(erro_mensagem)="{ value }">
              <span class="text-truncate d-inline-block" style="max-width:380px" :title="value">{{ value }}</span>
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </template>
  </div>
</template>

<script>
import JobErpClient from '@/api/Integracao/jobErp.client';

// Render function (não usa template inline para ser compatível com runtime-only build do Vue)
const KpiCard = {
  name: 'KpiCard',
  functional: true,
  props: {
    label:   { type: String, default: '' },
    value:   { type: [String, Number], default: null },
    sub:     { type: String, default: '' },
    icon:    { type: String, default: 'ActivityIcon' },
    variant: { type: String, default: 'primary' },
  },
  render(h, { props }) {
    const valor = props.value === null || props.value === undefined ? '—' : props.value;
    return h('b-card', { staticClass: 'kpi-card', props: { noBody: true } }, [
      h('b-card-body', {
        staticClass: 'd-flex align-items-center',
        staticStyle: { padding: '14px' },
      }, [
        h('div', {
          staticClass: 'kpi-icon',
          class: ['bg-light-' + (props.variant || 'primary')],
        }, [
          h('feather-icon', { props: { icon: props.icon || 'ActivityIcon', size: '20' } }),
        ]),
        h('div', { staticClass: 'ml-1' }, [
          h('div', { staticClass: 'kpi-value' }, [valor]),
          h('div', { staticClass: 'kpi-label text-muted' }, [props.label]),
          props.sub ? h('div', { staticClass: 'kpi-sub text-muted' }, [props.sub]) : null,
        ]),
      ]),
    ]);
  },
};

const client = new JobErpClient();

export default {
  name: 'DashboardIntegracao',
  components: { kpi: KpiCard },
  data() {
    return {
      carregando: false,
      data: null,
      janelaFields: [
        { key: 'janela',         label: 'Janela' },
        { key: 'total',          label: 'Execuções' },
        { key: 'sucesso',        label: 'Sucesso' },
        { key: 'erro',           label: 'Erro' },
        { key: 'taxa_sucesso',   label: 'Taxa sucesso' },
        { key: 'tempo_medio_ms', label: 'Tempo médio' },
      ],
      topJobsFields: [
        { key: 'nome',         label: 'Job' },
        { key: 'execucoes_7d', label: 'Execuções' },
        { key: 'sucessos',     label: 'OK' },
        { key: 'erros',        label: 'Erros' },
      ],
      errosFields: [
        { key: 'iniciado_em',   label: 'Quando' },
        { key: 'job_nome',      label: 'Job' },
        { key: 'origem',        label: 'Origem' },
        { key: 'status_http',   label: 'HTTP' },
        { key: 'tempo_ms',      label: 'Tempo' },
        { key: 'erro_mensagem', label: 'Mensagem' },
      ],
    };
  },
  computed: {
    janelas() {
      if (!this.data) return [];
      return [
        { janela: 'Últimas 24h', ...this.data.janelas.ultimas_24h },
        { janela: 'Últimos 7d',  ...this.data.janelas.ultimos_7d  },
        { janela: 'Últimos 30d', ...this.data.janelas.ultimos_30d },
      ];
    },
  },
  async created() { await this.carregar(); },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        const { data } = await client.dashboard();
        this.data = data.data;
      } catch (e) {
        this.$bvToast.toast(`Erro ao carregar dashboard: ${e.message || e}`, { title: 'Erro', variant: 'danger', solid: true });
      } finally {
        this.carregando = false;
      }
    },
    formatTaxa(v) {
      if (v === null || v === undefined) return '—';
      return `${v}%`;
    },
    formatMs(v) {
      if (v === null || v === undefined) return '—';
      const n = Number(v);
      if (!Number.isFinite(n)) return '—';
      if (n < 1000) return `${Math.round(n)} ms`;
      return `${(n / 1000).toFixed(2)} s`;
    },
    formatDate(v) {
      if (!v) return '—';
      try { return new Date(v).toLocaleString('pt-BR'); } catch (e) { return v; }
    },
    taxaVariant(v) {
      if (v === null || v === undefined) return 'light-secondary';
      if (v >= 95) return 'light-success';
      if (v >= 75) return 'light-warning';
      return 'light-danger';
    },
  },
};
</script>

<style scoped>
.page-breadcrumb { display: flex; align-items: center; font-size: 0.9rem; color: #6e6b7b; }
.kpi-card { margin-bottom: 0; }
.kpi-icon {
  width: 42px; height: 42px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
}
.kpi-value { font-size: 1.4rem; font-weight: 600; line-height: 1; }
.kpi-label { font-size: 0.75rem; margin-top: 2px; }
.kpi-sub   { font-size: 0.7rem; }
</style>
